/**
 * TowerBoomerang - Boomerang attack tower
 *
 * Uses a rotating bar that extends and retracts toward enemies.
 */
import { Vector } from '../../core/math/vector';
import { Line } from '../../core/math/line';
import { Circle } from '../../core/math/circle';
import { MyColor } from '../../entities/myColor';
import { Tower } from './tower';
import { TowerRegistry } from '../towerRegistry';
import { renderTowerBoomerang } from '../rendering/towerRenderer';
import { scaleSpeed, scalePeriod } from '../../core/speedScale';
import { isEnemy } from '@/game/player/ownership';
import type {
    VectorLike,
    CircleLike,
    MonsterLike as BaseMonsterLike,
    TerritoryLike,
    FogOfWarLike,
    UserLike,
    TowerLike,
} from '@/types/worldLike';

// Extended CircleLike with impact method
interface CircleLikeExt extends CircleLike {
    impact(other: CircleLike): boolean;
}

// Extended MonsterLike for boomerang tower (uses Vector for pos)
interface MonsterLike extends BaseMonsterLike {
    pos: Vector;
    getBodyCircle(): CircleLikeExt;
}

// WorldLike interface for TowerBoomerang
interface WorldLike {
    width: number;
    height: number;
    batterys: TowerLike[];
    territory?: TerritoryLike;
    fog?: FogOfWarLike;
    user: UserLike;
    getMonstersInRange(x: number, y: number, range: number): MonsterLike[];
    addBullet(bullet: unknown): void;
    removeBullet(bullet: unknown): void;
}

// 碰撞冷却帧数（约0.5秒 @ 60fps，考虑速度缩放）
const HIT_COOLDOWN_FRAMES = 30;

export class TowerBoomerang extends Tower {
    damage: number;
    barLen: number;
    barDis: number;
    barWidth: number;
    barRotateSelfSpeed: number;
    barDirect: Vector;
    bar: Line;
    // 记录每个怪物的最后受击帧，用于冷却判定
    private hitCooldown: WeakMap<object, number> = new WeakMap();

    constructor(x: number, y: number, world: any) {
        super(x, y, world);
        this.name = "回旋镖";
        this.clock = 1.1;

        this.damage = 1000;

        this.rangeR = 200;
        this.barLen = 20;
        this.barDis = this.rangeR;
        this.barWidth = 10;
        this.barRotateSelfSpeed = scaleSpeed(0.5);

        this.barDirect = Vector.randCircle();
        this.bar = this.initBar();
    }

    initBar(): Line {
        let barCenterLoc = this.pos.plus(Vector.randCircle().mul(this.barDis));
        let p1 = barCenterLoc.plus(this.barDirect.mul(this.barLen));
        let p2 = barCenterLoc.sub(this.barDirect.mul(this.barLen));

        let barLine = new Line(p1, p2);
        barLine.strokeColor = new MyColor(255, 124, 36, 0.8);
        barLine.strokeWidth = this.barWidth;
        return barLine;
    }

    barGo(): void {
        this.barDis = (Math.sin(this.liveTime / scalePeriod(10)) + 1) * this.rangeR;
        let barCenterLoc = this.pos.plus(this.dirction.mul(this.barDis));
        this.bar.moveTo(barCenterLoc);
    }

    barRotate(): void {
        let center = this.bar.getCenter();
        let a = this.barRotateSelfSpeed;
        let p1 = Vector.rotatePoint(center, this.bar.PosStart, a);
        let p2 = Vector.rotatePoint(center, this.bar.PosEnd, a);
        this.bar.resetLine(p1, p2);
    }

    getTarget(): void {
        const target = this.findFirstTarget();
        if (target) {
            this.dirction = target.pos.sub(this.pos).to1();
        }
    }

    barGoStep(): void {
        let barCenter = this.bar.PosStart.plus(this.bar.PosEnd).mul(0.5);
        // Use squared distance for length calculation (approximate for range query)
        let dx = this.bar.PosEnd.x - this.bar.PosStart.x;
        let dy = this.bar.PosEnd.y - this.bar.PosStart.y;
        let barLen = Math.sqrt(dx * dx + dy * dy);
        let nearbyMonsters = this.world.getMonstersInRange(barCenter.x, barCenter.y, barLen);
        let actualDamage = this.damage * this.getDamageMultiplier();
        for (let m of nearbyMonsters) {
            // Check fog first, using circle visibility for edge detection
            const mc = m.getBodyCircle();
            if (this.world.fog?.enabled && !this.world.fog.isCircleVisible(mc.x, mc.y, mc.r)) {
                continue;
            }
            if (this.bar.intersectWithCircle(mc)) {
                m.hpChange(-actualDamage, this.ownerId);
            }
        }
        this.barGo();
        this.barRotate();
    }

    goStep(): void {
        // 保持向后兼容：执行完整的更新逻辑
        this.goStepMove();
        this.goStepCollide();
    }

    /**
     * 移动阶段：处理旋转镖的移动和旋转
     */
    goStepMove(): void {
        super.goStepMove();
        this.barGo();
        this.barRotate();
    }

    /**
     * 碰撞阶段：处理旋转镖的碰撞检测
     */
    goStepCollide(): void {
        super.goStepCollide();
        this.getTarget();
        // 旋转镖碰撞检测
        let barCenter = this.bar.PosStart.plus(this.bar.PosEnd).mul(0.5);
        let dx = this.bar.PosEnd.x - this.bar.PosStart.x;
        let dy = this.bar.PosEnd.y - this.bar.PosStart.y;
        let barLen = Math.sqrt(dx * dx + dy * dy);
        let nearbyMonsters = this.world.getMonstersInRange(barCenter.x, barCenter.y, barLen);
        let actualDamage = this.damage * this.getDamageMultiplier();
        for (let m of nearbyMonsters) {
            // Filter friendly monsters (same owner)
            if (!isEnemy(this, m)) {
                continue;
            }
            const mc = m.getBodyCircle();
            if (this.world.fog?.enabled && !this.world.fog.isCircleVisible(mc.x, mc.y, mc.r)) {
                continue;
            }
            // 检查冷却，避免同一怪物每帧都受伤害
            const lastHit = this.hitCooldown.get(m) ?? -HIT_COOLDOWN_FRAMES;
            if (this.liveTime - lastHit < HIT_COOLDOWN_FRAMES) {
                continue;
            }
            if (this.bar.intersectWithCircle(mc)) {
                m.hpChange(-actualDamage, this.ownerId);
                this.hitCooldown.set(m, this.liveTime);
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        renderTowerBoomerang(this, ctx);
    }

    /**
     * 渲染塔主体（不含状态条）
     */
    renderBody(ctx: CanvasRenderingContext2D): void {
        this.bar.render(ctx);
        super.renderBody(ctx);
        let line = new Line(this.pos, this.bar.getCenter());
        line.strokeWidth = 0.1;
        line.strokeColor = this.bar.strokeColor;
        line.render(ctx);
    }
}

// Register class type for save system
TowerRegistry.registerClassType('TowerBoomerang', () => TowerBoomerang);
